/**
 * Drivers Service
 * Handles driver management operations
 */

import api from './api';

class DriversService {
  /**
   * Get all drivers with filters
   * @param {Object} filters - Filter options
   */
  async getDrivers(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.order) params.append('order', filters.order);

    const response = await api.get(`/driver?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch drivers');
  }

  /**
   * Get driver by ID
   * @param {string} driverId - Driver ID
   */
  async getDriverById(driverId) {
    const response = await api.get(`/driver/${driverId}`);

    if (response.success && response.data) {
      return response.data.driver || response.data;
    }

    throw new Error('Failed to fetch driver');
  }

  /**
   * Create new driver
   * @param {Object} driverData - Driver data
   */
  async createDriver(driverData) {
    const response = await api.post('/driver', driverData);

    if (response.success && response.data) {
      return response.data.driver || response.data;
    }

    throw new Error('Failed to create driver');
  }

  /**
   * Update driver
   * @param {string} driverId - Driver ID
   * @param {Object} updates - Updated data
   */
  async updateDriver(driverId, updates) {
    const response = await api.put(`/driver/${driverId}`, updates);

    if (response.success && response.data) {
      return response.data.driver || response.data;
    }

    throw new Error('Failed to update driver');
  }

  /**
   * Update driver status
   * @param {string} driverId - Driver ID
   * @param {string} status - Online, Offline, or Delayed
   */
  async updateStatus(driverId, status) {
    const response = await api.patch(`/driver/${driverId}/status`, { status });

    if (response.success && response.data) {
      return response.data.driver || response.data;
    }

    throw new Error('Failed to update driver status');
  }

  /**
   * Update driver location
   * @param {string} driverId - Driver ID
   * @param {number} latitude - Latitude
   * @param {number} longitude - Longitude
   */
  async updateLocation(driverId, latitude, longitude) {
    const response = await api.patch(`/driver/${driverId}/location`, {
      lat: latitude,
      long: longitude,
    });

    if (response.success && response.data) {
      return response.data.driver || response.data;
    }

    throw new Error('Failed to update driver location');
  }

  /**
   * Delete/deactivate driver
   * @param {string} driverId - Driver ID
   */
  async deleteDriver(driverId) {
    const response = await api.delete(`/driver/${driverId}`);

    if (response.success) {
      return true;
    }

    throw new Error('Failed to delete driver');
  }

  /**
   * Get driver statistics
   */
  async getStats() {
    const response = await api.get('/driver/stats');

    if (response.success && response.data) {
      return response.data.stats || response.data;
    }

    throw new Error('Failed to fetch driver stats');
  }

  /**
   * Get driver performance metrics and assigned jobs
   * @param {string} driverId - Driver ID
   */
  async getDriverPerformance(driverId) {
    const response = await api.get(`/driver/${driverId}/performance`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch driver performance');
  }

  /**
   * Get all drivers with their current job info (for dashboard/table)
   * @param {Object} filters - Filter options
   */
  async getDriversWithJobs(filters = {}) {
    const params = new URLSearchParams();

    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.order) params.append('order', filters.order);

    const response = await api.get(`/driver/with-jobs?${params.toString()}`);

    if (response.success && response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch drivers with jobs');
  }

  /**
   * Get available drivers (Online status)
   * @param {Object} options - Options
   */
  async getAvailableDrivers(options = {}) {
    return this.getDrivers({
      ...options,
      status: 'Online',
    });
  }

  /**
   * Get drivers near a location
   * @param {number} latitude - Latitude
   * @param {number} longitude - Longitude
   * @param {number} maxDistance - Maximum distance in meters
   */
  async getNearbyDrivers(latitude, longitude, maxDistance = 10000) {
    const params = new URLSearchParams();
    params.append('lat', latitude);
    params.append('long', longitude);
    params.append('maxDistance', maxDistance);

    const response = await api.get(`/driver/nearby?${params.toString()}`);

    if (response.success && response.data) {
      return response.data.drivers || response.data;
    }

    throw new Error('Failed to fetch nearby drivers');
  }
}

const driversService = new DriversService();
export default driversService;
